import { Injectable, inject } from '@angular/core';
import { AlertController } from '@ionic/angular/standalone';
import { devError } from '@core';
import { FirebaseError } from '@core/types/common.types';

/**
 * Centralized error handling service
 *
 * Provides consistent error handling across the app:
 * - Logs errors in dev mode only (GDPR/CCPA compliant)
 * - Shows user-friendly alerts
 * - Maps error codes to localized messages
 *
 * @example
 * ```typescript
 * try {
 *   await this.authService.sendOtp(phone);
 * } catch (error) {
 *   await this.errorHandler.handle('[PhoneEntry]', error);
 * }
 * ```
 */
@Injectable({
  providedIn: 'root'
})
export class ErrorHandlerService {
  private alertController = inject(AlertController);

  /**
   * Handle an error with logging and user alert
   *
   * **WI-028**: Type-safe error handling (replaced 'any' with union type)
   *
   * @param context - Component/service name for logging (e.g., '[PhoneEntry]')
   * @param error - Error object (FirebaseError, standard Error, or unknown)
   * @param customMessage - Optional custom user-facing message
   */
  async handle(context: string, error: Error | FirebaseError | unknown, customMessage?: string): Promise<void> {
    // Log error in dev mode only (H-02 compliance)
    devError(`${context} Error:`, error);

    // Get user-friendly message
    const message = customMessage || this.getErrorMessage(error);

    // Show alert to user
    const alert = await this.alertController.create({
      header: 'Something went wrong',
      message,
      buttons: ['OK']
    });
    await alert.present();
  }

  /**
   * Handle error silently (no alert, just logging)
   *
   * **WI-028**: Type-safe silent error handling
   *
   * Use for non-critical errors or background operations
   */
  handleSilent(context: string, error: Error | FirebaseError | unknown): void {
    devError(`${context} Error (silent):`, error);
  }

  /**
   * Handle non-recoverable error with user notification and callback action
   *
   * **WI-028**: Type-safe non-recoverable error handling
   *
   * Use for errors where the user needs to restart a process or navigate away
   *
   * @param context - Component/service name for logging
   * @param error - Error object (FirebaseError, standard Error, or unknown)
   * @param message - User-facing message explaining the issue
   * @param actionCallback - Function to call when user dismisses the alert (e.g., restart onboarding)
   * @returns Promise that resolves when user dismisses the alert
   */
  async handleNonRecoverable(
    context: string,
    error: Error | FirebaseError | unknown,
    message: string,
    actionCallback: () => void | Promise<void>
  ): Promise<void> {
    // Log error in dev mode only
    devError(`${context} Non-recoverable error:`, error);

    // Show alert with action
    const alert = await this.alertController.create({
      header: 'Session Expired',
      message,
      buttons: [
        {
          text: 'Start Over',
          handler: (): void => {
            // Call the callback after alert dismisses
            Promise.resolve(actionCallback());
          }
        }
      ],
      backdropDismiss: false // Force user to tap button
    });
    await alert.present();
  }

  /**
   * Map error codes to user-friendly messages
   *
   * **WI-012b Enhancement**:
   * - Added comprehensive Firebase Auth error mappings
   * - Prevents information leakage in production
   * - User-friendly messages for all common error scenarios
   * - Maps both custom error codes and Firebase error codes
   *
   * **WI-028**: Type-safe error message extraction with type guards
   */
  private getErrorMessage(error: Error | FirebaseError | unknown): string {
    // Type guard for FirebaseError (has 'code' property)
    if (this.isFirebaseError(error)) {
      const errorCode = error.code;
      return this.getMessageFromCode(errorCode);
    }

    // Type guard for standard Error (has 'message' property)
    if (error instanceof Error) {
      const errorCode = error.message;
      return this.getMessageFromCode(errorCode);
    }

    // Unknown error type - return generic message
    return 'Please try again';
  }

  /**
   * Type guard to check if error is a FirebaseError
   *
   * **WI-028**: Replace 'any' with type-safe guard
   */
  private isFirebaseError(error: unknown): error is FirebaseError {
    return error instanceof FirebaseError;
  }

  /**
   * Extract user-friendly message from error code
   *
   * **WI-028**: Extracted from getErrorMessage for type safety
   */
  private getMessageFromCode(errorCode: string): string {

    const errorMessages: Record<string, string> = {
      // Custom auth errors (from AuthService)
      'INVALID_PHONE_FORMAT': 'Please enter a valid phone number starting with +',
      'PHONE_REQUIRED': 'Phone number is required',
      'TOO_MANY_ATTEMPTS': 'Too many attempts. Please try again later',
      'INVALID_OTP': 'Invalid code. Please check and try again',
      'OTP_EXPIRED': 'Code expired. Please request a new one',
      'SESSION_EXPIRED': 'Your session expired. Please start over',
      'RATE_LIMITED': 'Too many requests. Please try again later',
      'RATE_LIMIT_EXCEEDED': 'Too many attempts. Please wait an hour and try again',

      // Firebase Auth errors (auth/*)
      'auth/invalid-phone-number': 'Please enter a valid phone number',
      'auth/missing-phone-number': 'Phone number is required',
      'auth/quota-exceeded': 'Too many requests. Please try again tomorrow',
      'auth/invalid-verification-code': 'Invalid code. Please check and try again',
      'auth/invalid-verification-id': 'Session expired. Please request a new code',
      'auth/code-expired': 'Code expired. Please request a new one',
      'auth/too-many-requests': 'Too many attempts. Please wait and try again',
      'auth/user-disabled': 'This account has been disabled',
      'auth/operation-not-allowed': 'Phone authentication is not available',
      'auth/network-request-failed': 'Network error. Please check your connection',
      'auth/captcha-check-failed': 'Verification failed. Please try again',
      'auth/missing-verification-code': 'Please enter the verification code',
      'auth/credential-already-in-use': 'This phone number is already in use',

      // Firestore errors
      'PERMISSION_DENIED': 'You don\'t have permission to access this data',
      'NOT_FOUND': 'The requested data was not found',
      'ALREADY_EXISTS': 'This data already exists',
      'UNAVAILABLE': 'Service temporarily unavailable. Please try again',
      'permission-denied': 'You don\'t have permission to access this data',
      'not-found': 'The requested data was not found',
      'unavailable': 'Service temporarily unavailable. Please try again',

      // Cloud Functions errors
      'functions/invalid-argument': 'Invalid request. Please check your input',
      'functions/unauthenticated': 'Please sign in to continue',
      'functions/permission-denied': 'You don\'t have permission to perform this action',
      'functions/not-found': 'The requested resource was not found',
      'functions/internal': 'Server error. Please try again',
      'functions/unavailable': 'Service temporarily unavailable. Please try again',

      // Network errors
      'NETWORK_ERROR': 'Network error. Please check your connection',
      'TIMEOUT': 'Request timed out. Please try again',

      // Generic
      'UNKNOWN': 'Something went wrong. Please try again'
    };

    return errorMessages[errorCode] || 'Please try again';
  }
}
